import { FileText, Coffee, CheckCircle2, Wallet, ShieldCheck, Clock, HeartHandshake, MessageCircle } from "lucide-react";
import { Button } from "../ui/button";
import { openLiveChat } from "../../lib/chat";

const steps = [
  {
    num: "01",
    icon: FileText,
    title: "Apply in 5 Minutes",
    desc: "Fill out our quick online application. No novels required — just the basics about your business and what you need.",
  },
  {
    num: "02",
    icon: Coffee,
    title: "We Review (While You Relax)",
    desc: "Our team reviews your application within 2 hours. Go grab a coffee, watch a show, or take a nap. We got this.",
  },
  {
    num: "03",
    icon: CheckCircle2,
    title: "Get Approved Fast",
    desc: "Receive your approval with clear terms and rates. No confusing jargon — we explain everything in plain English.",
  },
  {
    num: "04",
    icon: Wallet,
    title: "Receive Your Funds",
    desc: "Money hits your account, usually within 24 hours. Time to put those plans into action and grow that business!",
  },
];

const badges = [
  { icon: Clock, label: "2-Hour Response" },
  { icon: HeartHandshake, label: "All Credit Welcome" },
  { icon: ShieldCheck, label: "No Hidden Fees" },
];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative py-24 md:py-32 bg-secondary/50"
    >
      <div className="container-tight">
        <div className="max-w-3xl mb-14 md:mb-16">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#F5B82E]">
            Simple Process
          </span>
          <h2
            data-testid="how-it-works-headline"
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-foreground leading-[1.05] text-balance"
          >
            How It Works
          </h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-xl">
            Four easy steps from application to funding. Our team keeps it
            simple because you&apos;ve got a business to run.
          </p>
        </div>

        {/* 4-step grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div
              key={s.num}
              data-testid={`step-${s.num}`}
              className="relative bg-card border border-border rounded-3xl p-7 flex flex-col transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(0,0,0,0.07)]"
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-5xl font-black text-[#F5B82E] leading-none">
                  {s.num}
                </span>
                <div className="h-11 w-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <s.icon className="h-5 w-5" strokeWidth={2.2} />
                </div>
              </div>
              <h3 className="mt-6 font-display text-xl font-bold text-foreground leading-snug">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {s.desc}
              </p>
              {/* subtle connector line */}
              {i < steps.length - 1 && (
                <div
                  aria-hidden
                  className="hidden lg:block absolute top-12 -right-3 w-6 h-px bg-border"
                />
              )}
            </div>
          ))}
        </div>

        {/* Trust badges strip */}
        <div className="mt-14 flex flex-wrap justify-center gap-3 sm:gap-4">
          {badges.map((b) => (
            <div
              key={b.label}
              data-testid={`trust-badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-2.5 rounded-full bg-card border border-border px-5 py-3 shadow-[0_4px_14px_rgb(0,0,0,0.03)]"
            >
              <div className="h-7 w-7 rounded-full bg-[#F5B82E]/15 flex items-center justify-center text-[#C9951E]">
                <b.icon className="h-3.5 w-3.5" strokeWidth={2.4} />
              </div>
              <span className="text-sm font-semibold text-foreground">
                {b.label}
              </span>
            </div>
          ))}
        </div>

        <div
          data-testid="how-it-works-cta"
          className="mt-12 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4"
        >
          <Button
            className="rounded-full h-12 px-8 bg-accent hover:bg-[#D1664A] text-accent-foreground font-semibold shadow-[0_4px_14px_0_rgb(224,122,95,0.35)]"
            onClick={() =>
              document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" })
            }
          >
            Start your application
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-full h-12 px-8 font-semibold border-primary/30 text-primary hover:bg-primary/5"
            onClick={openLiveChat}
            data-testid="how-it-works-chat"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Ask a human first
          </Button>
        </div>
      </div>
    </section>
  );
}
